import { useMemo } from 'react';
import type { AskBlock, ChatMessage } from '@/api/models';
import { isClientAuthorKind } from '@/api/models';
import { UnknownTurn } from '@/components/UnknownTurn';
import { cn } from '@/lib/cn';
import { RunGlyph } from './ledger/Rail';
import { RunStop } from './ledger/RunStop';
import type { LedgerStop } from './ledger/projection';
import type { UnknownEvent } from './stream/stream-store';
import { AgentBlocks, ClientTurn, StreamingText } from './turns';
import { AskCard } from './AskCard';

/**
 * The thread.
 *
 * Structurally this is the Run Ledger: what the client asked sits in a panel
 * card, and each run the platform carried out for them sits under it as one
 * timed block that opens to show its stages. Reading only the block headings
 * tells you how many times you asked for something, how long each took, and how
 * each ended.
 */

type ThreadItem =
  | { kind: 'client'; key: string; message: ChatMessage }
  | { kind: 'run'; key: string; runId: number; stop: LedgerStop | null; messages: ChatMessage[] }
  | { kind: 'unknown'; key: string; token: string; context: Record<string, string | number> };

/**
 * Fold the message list and the live ledger into one ordered list of items.
 *
 * Messages carry the ordering (a dense per-visibility `seq`); the ledger
 * carries the timing and the phases. A run appears at the position of its first
 * message, or - for a run that has not spoken yet - at the end, where it just
 * started.
 */
function buildThread(messages: ChatMessage[], stops: LedgerStop[]): ThreadItem[] {
  const stopsByRun = new Map<number, LedgerStop>();
  for (const stop of stops) stopsByRun.set(stop.runId, stop);

  const items: ThreadItem[] = [];
  const runIndexInItems = new Map<number, number>();
  const placedRuns = new Set<number>();

  for (const message of messages) {
    if (!isClientAuthorKind(message.author_kind)) {
      // An `expert` author on a client surface is invariant I7 failing. The
      // server cannot return one, so this is a leak detector, not a fallback.
      items.push({
        kind: 'unknown',
        key: `unknown-author-${message.id}`,
        token: String(message.author_kind).slice(0, 48),
        context: { messageId: message.id, seq: message.seq },
      });
      continue;
    }

    if (message.author_kind === 'client') {
      items.push({ kind: 'client', key: `msg-${message.id}`, message });
      continue;
    }

    const runId = message.run_id ?? null;
    if (runId === null) {
      // A system turn with no run behind it - a platform notice. It gets its
      // own single-message stop so it still sits on the rail.
      items.push({ kind: 'run', key: `sys-${message.id}`, runId: -message.id, stop: null, messages: [message] });
      continue;
    }

    const existing = runIndexInItems.get(runId);
    if (existing !== undefined) {
      const item = items[existing];
      if (item && item.kind === 'run') item.messages.push(message);
      continue;
    }

    runIndexInItems.set(runId, items.length);
    placedRuns.add(runId);
    items.push({
      kind: 'run',
      key: `run-${runId}`,
      runId,
      stop: stopsByRun.get(runId) ?? null,
      messages: [message],
    });
  }

  // A run that has started but not yet produced a turn - the common case for
  // the first thirty seconds, and precisely when a client is watching hardest.
  for (const stop of stops) {
    if (placedRuns.has(stop.runId)) continue;
    items.push({ kind: 'run', key: `run-${stop.runId}`, runId: stop.runId, stop, messages: [] });
  }

  return items;
}

interface ThreadProps {
  projectId: number;
  messages: ChatMessage[];
  stops: LedgerStop[];
  deltas: Record<number, string>;
  pendingAsk: { ask: AskBlock; answerableByClient: boolean } | null;
  unknownEvents: UnknownEvent[];
  className?: string;
}

export function Thread({
  projectId,
  messages,
  stops,
  deltas,
  pendingAsk,
  unknownEvents,
  className,
}: ThreadProps) {
  const items = useMemo(() => buildThread(messages, stops), [messages, stops]);

  // Requests are a real sequence, so numbering them carries information: it is
  // how a client refers to "the second thing I asked for".
  let requestNumber = 0;

  const liveRunId = stops.find((s) => s.outcome === 'running')?.runId ?? null;
  const deltaEntries = Object.entries(deltas);

  return (
    <ol className={cn('relative', className)}>
      {items.map((item) => {
        if (item.kind === 'unknown') {
          return (
            <li key={item.key}>
              <UnknownTurn leakKind="unknown_author_kind" token={item.token} context={item.context} />
            </li>
          );
        }

        if (item.kind === 'client') {
          return <ClientTurn key={item.key} message={item.message} />;
        }

        requestNumber += 1;
        const isLive = item.runId === liveRunId;
        const stop = item.stop;

        const body = (
          <>
            {item.messages.map((message) => (
              <div key={message.id} className="mb-[22px] px-0.5 last:mb-0">
                <AgentBlocks message={message} />
              </div>
            ))}

            {/* Assistant text still arriving. The canonical `message` frame
                replaces it when the turn closes. */}
            {isLive
              ? deltaEntries.map(([seq, text]) =>
                  text ? <StreamingText key={`delta-${seq}`} text={text} /> : null,
                )
              : null}

            {/* The parked question, anchored to its run so it cannot drift away
                from the work it belongs to. */}
            {isLive && pendingAsk ? (
              <AskCard
                projectId={projectId}
                ask={pendingAsk.ask}
                answerableByClient={pendingAsk.answerableByClient}
              />
            ) : null}
          </>
        );

        if (!stop) {
          return (
            <HistoricalStop key={item.key} index={requestNumber} messages={item.messages}>
              {body}
            </HistoricalStop>
          );
        }

        return (
          <RunStop key={item.key} stop={stop} index={requestNumber}>
            {body}
          </RunStop>
        );
      })}

      {unknownEvents.map((event) => (
        <li key={`unknown-${event.id}`}>
          <UnknownTurn leakKind="unknown_stream_event" token={event.kind} context={{ at: event.at }} />
        </li>
      ))}
    </ol>
  );
}

/**
 * A run from before this session opened.
 *
 * We have its turns but not its phase timings - those lived on a stream nobody
 * was listening to. So it draws the block head and stops there. Inventing
 * segment durations for it would put fiction on a ledger whose entire value is
 * that it is measured, and there is nothing to expand into: the "Show steps"
 * affordance is absent rather than present and empty.
 */
function HistoricalStop({
  index,
  messages,
  children,
}: {
  index: number;
  messages: ChatMessage[];
  children: React.ReactNode;
}) {
  const first = messages[0];
  const stamp = first ? new Date(first.created_at) : null;

  return (
    <li>
      <div className="mb-[18px] flex items-center gap-3 rounded-lg bg-bone px-[18px] py-3.5">
        <RunGlyph outcome="succeeded" />
        <span className="min-w-0 flex-1">
          <span className="block text-[0.96875rem] font-semibold tracking-[-0.015em] text-graphite-900">
            Finished earlier
          </span>
          <span className="mt-0.5 block font-mono text-[0.6875rem] tabular-nums text-graphite-300">
            Request {String(index).padStart(2, '0')}
            {stamp
              ? ` · ${stamp.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}`
              : ''}
          </span>
        </span>
      </div>
      {children}
    </li>
  );
}
